import { createGlobalStyle } from 'styled-components';
import { QUERIES } from '../constants/mediaQueries';

const GlobalStyles = createGlobalStyle`
    /* =========================
       1. CSS Variables
    ========================= */
    :root {
        /* Colors */
        --color-neutral-white: #FEFEFE;
        --color-neutral-lightest: #F8F7F5;
        --color-neutral-lighter: #E5E3E0;
        --color-neutral-light: #C7C4C0;
        --color-neutral: #8B8884;
        --color-neutral-dark: #5A5754;
        --color-neutral-darker: #2A2824;
        --color-neutral-darkest: #0F0E0C;

        /* Primary Colors */
        --color-primary-lightest: #E8EDF5;
        --color-primary-lighter: #D1DEEB;
        --color-primary-light: #5A7BA3;
        --color-primary: #0B1F3A;
        --color-primary-dark: #09192E;
        --color-primary-darker: #071422;
        --color-primary-darkest: #040A11;

        /* Secondary Colors */
        --color-secondary-lightest: #FFF7E6;
        --color-secondary-lighter: #FFEDCC;
        --color-secondary-light: #FFCF4D;
        --color-secondary: #FFBA08;
        --color-secondary-dark: #E6A707;
        --color-secondary-darker: #B8850A;
        --color-secondary-darkest: #8A640A;

        /* Social Media Colors */
        --color-whatsapp: #25D366;
        --color-linkedin: #0077b5;
        --color-github: #333333;

        /* Opacity */
        --opacity-color-white-01: rgba(255, 255, 255, 0.01);
        --opacity-color-white-05: rgba(255,255,255,0.05);
        --opacity-color-white-08: rgba(255, 255, 255, 0.08);
        --opacity-color-white-20: rgba(255,255,255,0.2);
        --opacity-color-neutral-darkest-15: rgba(13,10,3,0.15);

        /* Typography */
        --font-family: "Ubuntu", system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
        --font-family-emphasis: "Zen Maru Gothic", "Ubuntu", Georgia, Cambria, Verdana, sans-serif, serif;
        
        /* Font Weights */
        --font-weight-light: 300;
        --font-weight-regular: 400;
        --font-weight-medium: 500;
        --font-weight-semibold: 600;
        --font-weight-bold: 700;
        --font-weight-heavy: 800;

        /* Heading Sizes (Desktop) */
        --heading1-size: 3rem;
        --heading1-line: 1.2;
        --heading2-size: 2.3rem;
        --heading2-line: 1.2;
        --heading3-size: 2.25rem;
        --heading3-line: 1.2;
        --heading4-size: 2rem;
        --heading4-line: 1.3;
        --heading5-size: 1.5rem;
        --heading5-line: 1.4;
        --heading6-size: 1.25rem;
        --heading6-line: 1.4;
        --tagline-size: 1rem;
        --tagline-line: 1.5;

        /* Body Sizes (Desktop) */
        --body-large-size: 1.25rem;
        --body-base-size: 1rem;
        --body-small-size: 0.875rem;
        --body-tiny-size: 0.75rem;
        --body-line: 1.5;

        /* Heading Sizes (Tablet) */
        --heading1-size-tablet: 2.75rem;
        --heading2-size-tablet: 2.25rem;
        --heading3-size-tablet: 2rem;
        --heading4-size-tablet: 1.75rem;
        --heading5-size-tablet: 1.25rem;
        --heading6-size-tablet: 1.125rem;
        --tagline-size-tablet: 0.9rem;

        /* Body Sizes (Tablet) */
        --body-large-size-tablet: 1.125rem;
        --body-base-size-tablet: 0.9375rem;
        --body-small-size-tablet: 0.8125rem;
        --body-tiny-size-tablet: 0.75rem;

        /* Heading Sizes (Mobile) */
        --heading1-size-mobile: 2rem;
        --heading2-size-mobile: 1.625rem;
        --heading3-size-mobile: 1.375rem;
        --heading4-size-mobile: 1.125rem;
        --heading5-size-mobile: 1rem;
        --heading6-size-mobile: 0.9375rem;
        --tagline-size-mobile: 0.8125rem;

        /* Body Sizes (Mobile) */
        --body-large-size-mobile: 1.125rem;
        --body-base-size-mobile: 0.875rem;
        --body-small-size-mobile: 0.75rem;
        --body-tiny-size-mobile: 0.6875rem;

        /* Spacing */
        --spacing-xsmall: 0.3rem;
        --spacing-small: 0.5rem;
        --spacing-base: 1rem;
        --spacing-large: 1.5rem;
        --spacing-xlarge: 2rem;

        /* Containers */
        --width-container-xsmall: 150px;
        --width-container-small: 200px;
        --width-container-base: 300px;
        --width-container-large: 500px;
        --width-container-xlarge: 650px;
        --height-container-small: 300px;
        --height-container-base: 400px;
        --height-container-large: 500px;
        
        /* Layout */
        --height-text-area: 150px;
        --height-header: 100px;
        --height-footer: 4rem;
        --max-height-accordion: 500px;
        --width-icon-button: 40px;
        --width-icon-button-offset: -20px;
        --width-scrollbar: 10px;
        --width-section: 80vw;
        --width-logo: 120px;

        /* Button Specific Styles */
        --button-padding-v: 0.75rem;
        --button-padding-h: 1.5rem;
        --button-padding-icon: 0.5rem;
        --button-gap-standard: 0.5rem;
        --button-gap-icon: 0.375rem;
        --button-icon-only-size: 2.5rem;
        --button-icon-size: 1.25rem;
        --button-active-indicator-height: 3px;
        --button-focus-outline-width: 2px;
        --button-focus-outline-offset: 2px;

        /* Animations & Transitions */
        --hover-scale: scale(1.05);
        --hover-translate-x: translateX(-3px);
        --hover-translate-y: translateY(-3px);
        --box-shadow-subtle: 0 2px 4px rgba(0, 0, 0, 0.05);
        --box-shadow-medium: 0 6px 8px rgba(0, 0, 0, 0.15);
        --box-shadow-button-hover: 0 4px 8px rgba(0, 0, 0, 0.2);
        --transition-all-ease: all 0.3s ease;
        --transition-accordion: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        --hover-scale-button: scale(1.03);
        --hover-scale-button-icon: scale(1.08);
        
        /* Effects & Decorations */
        --radius-base: 0.5rem;
        --radius-large: 1.5rem;
        --radius-circle: 50%;
        --radius-button: 0.5rem;
        --radius-input: 0.5rem;
        --border-width: 2px;
        --border-transparent: var(--border-width) solid transparent;
        --border-default: var(--border-width) solid var(--color-primary-dark);
        --border-accent: var(--border-width) solid var(--color-primary);
        --shadow-default: 0 0.5rem 1rem var(--color-neutral-light);

        /*
        =========================
        Scheme 1: Standard Light Mode
        - Clean white background with subtle blue accents
        - Used for default light theme
        =========================
        */
        --scheme1-text: var(--color-primary-darkest);       
        --scheme1-background: var(--color-neutral-white);   
        --scheme1-accent: var(--color-primary);             
        --scheme1-foreground: var(--color-neutral-lightest);      
        --scheme1-border-default: var(--border-width) solid var(--color-primary-lighter);
        --scheme1-border-accent: var(--border-width) solid var(--color-primary-light);
        --scheme1-shadow-default: 0 0.25rem 0.75rem rgba(11, 31, 58, 0.08);
        --scheme1-hover-text: var(--color-neutral-white);
        --scheme1-hover-background: var(--color-primary);
        --scheme1-hover-border: var(--border-width) solid var(--color-primary);
        --scheme1-focus-outline: var(--color-primary-light);
        --scheme1-active-text: var(--color-neutral-white);
        --scheme1-active-background: var(--color-primary-dark);
        --scheme1-active-border: var(--border-width) solid var(--color-primary-dark);
        --scheme1-input-background: var(--color-neutral-lightest);
        --scheme1-input-border: var(--border-width) solid var(--color-primary-light);
        --scheme1-input-color: var(--color-primary-darkest);
        --scheme1-input-placeholder: var(--color-primary-dark);
        --scheme1-input-focus-border: var(--color-primary);
        --scheme1-input-focus-shadow: 0 0 0 3px rgba(11, 31, 58, 0.15);
        --scheme1-input-autofill-background: var(--color-primary-lightest);
        --scheme1-input-autofill-color: var(--color-primary-darkest);
        
        /*
        =========================
        Scheme 2: Rich Dark Mode
        - Deep blue background with warm yellow highlights
        - Used for default dark theme
        =========================
        */
        --scheme2-text: var(--color-neutral-lightest);          
        --scheme2-background: var(--color-primary);
        --scheme2-accent: var(--color-secondary);       
        --scheme2-foreground: var(--color-primary-dark);
        --scheme2-border-default: var(--border-width) solid var(--color-primary);
        --scheme2-border-accent: var(--border-width) solid var(--color-secondary);
        --scheme2-shadow-default: 0 0.25rem 0.75rem rgba(255, 186, 8, 0.1);
        --scheme2-hover-text: var(--color-primary-darkest);
        --scheme2-hover-background: var(--color-secondary);
        --scheme2-hover-border: var(--border-width) solid var(--color-secondary);
        --scheme2-focus-outline: var(--color-secondary-light);
        --scheme2-active-text: var(--color-primary-darkest);
        --scheme2-active-background: var(--color-secondary-dark);
        --scheme2-active-border: var(--border-width) solid var(--color-secondary-darker);
        --scheme2-input-background: var(--color-primary-darker);
        --scheme2-input-border: var(--border-width) solid var(--color-primary);
        --scheme2-input-color: var(--color-neutral-lightest);
        --scheme2-input-placeholder: var(--color-neutral);
        --scheme2-input-focus-border: var(--color-secondary);
        --scheme2-input-focus-shadow: 0 0 0 3px rgba(255, 186, 8, 0.2);
        --scheme2-input-autofill-background: var(--color-primary-dark);
        --scheme2-input-autofill-color: var(--color-neutral-lightest);
        
        /*
        =========================
        Scheme 3: Bold Brand Mode
        - Vibrant blue and yellow with strong contrast
        - Used for brand-heavy or marketing sections
        =========================
        */
        --scheme3-text: var(--color-primary-darkest);      
        --scheme3-background: var(--color-secondary-lightest);
        --scheme3-accent: var(--color-primary);       
        --scheme3-foreground: var(--color-secondary-lighter);  
        --scheme3-border-default: var(--border-width) solid var(--color-secondary-light);
        --scheme3-border-accent: var(--border-width) solid var(--color-primary);
        --scheme3-shadow-default: 0 0.25rem 0.75rem rgba(11, 31, 58, 0.12);
        --scheme3-hover-text: var(--color-neutral-white);
        --scheme3-hover-background: var(--color-primary);
        --scheme3-hover-border: var(--border-width) solid var(--color-primary-dark);
        --scheme3-focus-outline: var(--color-primary-light);
        --scheme3-active-text: var(--color-primary-darker); 
        --scheme3-active-background: var(--color-secondary); 
        --scheme3-active-border: var(--border-width) solid var(--color-primary-darker);
        --scheme3-input-background: var(--color-secondary-lightest); 
        --scheme3-input-border: var(--border-width) solid var(--color-secondary-light);
        --scheme3-input-color: var(--color-primary-darkest); 
        --scheme3-input-placeholder: var(--color-primary-light); 
        --scheme3-input-focus-border: var(--color-primary);
        --scheme3-input-focus-shadow: 0 0 0 3px rgba(11, 31, 58, 0.15);
        --scheme3-input-autofill-background: var(--color-secondary-lightest);
        --scheme3-input-autofill-color: var(--color-primary-darkest);
        
        /*
        =========================
        Scheme 4: Subtle Glass Overlay
        - Semi-transparent white with soft blue borders
        - For overlays on light backgrounds
        =========================
        */
        --scheme4-text: var(--color-neutral-darkest);
        --scheme4-background: rgba(255, 255, 255, 0.5);
        --scheme4-accent: var(--color-primary-light);
        --scheme4-foreground: rgba(255, 255, 255, 0.7);
        --scheme4-border-default: var(--border-width) solid rgba(11, 31, 58, 0.15);
        --scheme4-border-accent: var(--border-width) solid rgba(11, 31, 58, 0.3);
        --scheme4-shadow-default: 0 0.5rem 1.25rem rgba(0, 0, 0, 0.05);
        --scheme4-hover-text: var(--color-neutral-darkest);
        --scheme4-hover-background: rgba(255, 255, 255, 0.7);
        --scheme4-hover-border: var(--border-width) solid var(--color-primary-light);
        --scheme4-focus-outline: var(--color-primary-light);
        --scheme4-active-text: var(--color-neutral-white);
        --scheme4-active-background: var(--color-primary);
        --scheme4-active-border: var(--border-width) solid var(--color-primary);
        --scheme4-input-background: rgba(255, 255, 255, 0.6);
        --scheme4-input-border: var(--border-width) solid rgba(11, 31, 58, 0.2);
        --scheme4-input-color: var(--color-neutral-darkest);
        --scheme4-input-placeholder: var(--color-neutral-dark);
        --scheme4-input-focus-border: var(--color-primary-light);
        --scheme4-input-focus-shadow: 0 0 0 3px rgba(11, 31, 58, 0.1);
        --scheme4-input-autofill-background: rgba(255, 255, 255, 0.65);
        --scheme4-input-autofill-color: var(--color-neutral-darkest);

        /*
        =========================
        Scheme 5: Dark Frosted Glass Overlay
        - Transparent with dark text and subtle blue accents
        - For overlays on dark images or video backgrounds
        =========================
        */
        --scheme5-text: var(--color-neutral-white);
        --scheme5-background: rgba(10, 15, 20, 0.5);
        --scheme5-accent: var(--color-primary-lightest);
        --scheme5-foreground: rgba(255, 255, 255, 0.3);
        --scheme5-border-default: var(--border-width) solid rgba(255, 255, 255, 0.2);
        --scheme5-border-accent: var(--border-width) solid rgba(255, 255, 255, 0.35);
        --scheme5-shadow-default: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.3);
        --scheme5-hover-text: var(--color-neutral-white);
        --scheme5-hover-background: rgba(255, 255, 255, 0.2);
        --scheme5-hover-border: var(--border-width) solid var(--color-primary-lightest);
        --scheme5-focus-outline: var(--color-primary-lightest);
        --scheme5-active-text: var(--color-neutral-white);
        --scheme5-active-background: rgba(255, 255, 255, 0.3);
        --scheme5-active-border: var(--border-width) solid var(--color-primary-lightest);
        --scheme5-input-background: rgba(255, 255, 255, 0.15);
        --scheme5-input-border: var(--border-width) solid rgba(255, 255, 255, 0.25);
        --scheme5-input-color: var(--color-neutral-white);
        --scheme5-input-placeholder: var(--color-neutral-light);
        --scheme5-input-focus-border: var(--color-primary-lightest);
        --scheme5-input-focus-shadow: 0 0 0 3px rgba(255, 255, 255, 0.15);
        --scheme5-input-autofill-background: rgba(255, 255, 255, 0.2);
        --scheme5-input-autofill-color: var(--color-neutral-white);

        /*
        =========================
        Scheme 6: Frosted Glass Overlay
        - Transparent with white text and blue accents
        - For overlays on images or video backgrounds
        =========================
        */
        --scheme6-text: var(--color-neutral-white);
        --scheme6-background: rgba(255, 255, 255, 0.15);
        --scheme6-accent: var(--color-primary-lightest);
        --scheme6-foreground: rgba(255, 255, 255, 0.35);
        --scheme6-border-default: var(--border-width) solid rgba(255, 255, 255, 0.25);
        --scheme6-border-accent: var(--border-width) solid rgba(255, 255, 255, 0.4);
        --scheme6-shadow-default: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.25);
        --scheme6-hover-text: var(--color-neutral-white);
        --scheme6-hover-background: rgba(255, 255, 255, 0.25);
        --scheme6-hover-border: var(--border-width) solid var(--color-primary-lightest);
        --scheme6-focus-outline: var(--color-primary-lightest);
        --scheme6-active-text: var(--color-neutral-white);
        --scheme6-active-background: rgba(255, 255, 255, 0.3);
        --scheme6-active-border: var(--border-width) solid var(--color-primary-lightest);
        --scheme6-input-background: rgba(255, 255, 255, 0.2);
        --scheme6-input-border: var(--border-width) solid rgba(255, 255, 255, 0.3);
        --scheme6-input-color: var(--color-neutral-white);
        --scheme6-input-placeholder: var(--color-neutral-light);
        --scheme6-input-focus-border: var(--color-primary-lightest);
        --scheme6-input-focus-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
        --scheme6-input-autofill-background: rgba(255, 255, 255, 0.25);
        --scheme6-input-autofill-color: var(--color-neutral-white);

    }

    /* =========================
       2. Responsive Variables
    ========================= */
    ${QUERIES.maxTablet} {
        :root {
            --heading1-size: var(--heading1-size-tablet);
            --heading2-size: var(--heading2-size-tablet);
            --heading3-size: var(--heading3-size-tablet);
            --heading4-size: var(--heading4-size-tablet);
            --heading5-size: var(--heading5-size-tablet);
            --heading6-size: var(--heading6-size-tablet);
            --tagline-size: var(--tagline-size-tablet);

            --body-large-size: var(--body-large-size-tablet);
            --body-base-size: var(--body-base-size-tablet);
            --body-small-size: var(--body-small-size-tablet);
            --body-tiny-size: var(--body-tiny-size-tablet);

            --spacing-xsmall: 0.25rem;
            --spacing-small: 0.45rem;
            --spacing-base: 0.8rem;
            --spacing-large: 1.25rem;
            --spacing-xlarge: 1.7rem;

            --width-container-xsmall: 130px;
            --width-container-small: 180px;
            --width-container-base: 250px;
            --width-container-large: 400px;
            --width-container-xlarge: 580px;
            --height-container-small: 220px;
            --height-container-base: 300px;

            --height-header: 80px;
            --height-footer: 3.5rem;
            --width-section: 90vw;
            --width-logo: 100px;
            
            --button-padding-v: 0.65rem;
            --button-padding-h: 1.25rem;
            --button-icon-only-size: 2.25rem;
            --button-icon-size: 1.125rem;
        }
    }

    ${QUERIES.maxMobile} {
        :root {
            --heading1-size: var(--heading1-size-mobile);
            --heading2-size: var(--heading2-size-mobile);
            --heading3-size: var(--heading3-size-mobile);
            --heading4-size: var(--heading4-size-mobile);
            --heading5-size: var(--heading5-size-mobile);
            --heading6-size: var(--heading6-size-mobile);
            --tagline-size: var(--tagline-size-mobile);

            --body-large-size: var(--body-large-size-mobile);
            --body-base-size: var(--body-base-size-mobile);
            --body-small-size: var(--body-small-size-mobile);
            --body-tiny-size: var(--body-tiny-size-mobile);
            --body-line: 1.5;

            --spacing-xsmall: 0.2rem;
            --spacing-small: 0.4rem;
            --spacing-base: 0.6rem;
            --spacing-large: 1rem;
            --spacing-xlarge: 1.4rem;

            --width-container-xsmall: 100px;
            --width-container-small: 160px;
            --width-container-base: 200px;
            --width-container-large: 300px;
            --width-container-xlarge: 500px;
            --height-container-small: 200px;
            --height-container-base: 250px;

            --height-footer: 3rem;
            --width-section: 95vw;
            --width-logo: 80px;

            --button-padding-v: 0.5rem;
            --button-padding-h: 1rem;
            --button-icon-only-size: 2rem;
            --button-icon-size: 1rem;
        }
    }

    /* =========================
       3. Reset & Base Styles
    ========================= */
    *, *::before, *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    /* =========================
       4. Typography
    ========================= */
    html, body {
        color: var(--scheme1-text);
        background-color: var(--scheme1-background);
        font-family: var(--font-family);
        font-size: var(--body-base-size);
        line-height: var(--body-line);
        height: 100%;
    }

    h1, h2, h3, h4, h5, h6 {
        font-family: var(--font-family-emphasis);
        font-weight: var(--font-weight-regular);
        line-height: 1.2;
        color: var(--scheme1-text);
        margin: var(--spacing-base) 0;
        text-transform: uppercase;
    }

    h1 {
        font-size: var(--heading1-size);
        line-height: var(--heading1-line);
        font-weight: var(--font-weight-bold);
    }

    h2 {
        font-size: var(--heading2-size);
        line-height: var(--heading2-line);
    }

    h3 {
        font-size: var(--heading3-size);
        line-height: var(--heading3-line);
    }

    h4 {
        font-size: var(--heading4-size);
        line-height: var(--heading4-line);
    }

    h5 {
        font-size: var(--heading5-size);
        line-height: var(--heading5-line);
    }

    h6 {
        font-size: var(--heading6-size);
        line-height: var(--heading6-line);
    }

    body, p {
        font-family: var(--font-family);
        font-size: var(--body-base-size);
        line-height: var(--body-line);
        color: var(--scheme1-text);
    }

    p {
        opacity: 1;
    }

    mark {
        background: var(--color-primary-light); 
    }

    a {
        color: var(--scheme4-text);
        cursor: pointer;
        text-decoration: none;
        transition: color 0.2s;
        &:hover {
            color: var(--scheme4-hover-text);
        }
    }

    label {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }

    /* =========================
       5. Layout & Containers
    ========================= */
    section {
        padding: var(--spacing-xlarge) 0;
        width: 100%;
    }

    img {
        max-width: 100%;
        height: auto;
    }

    /* =========================
       6. Elements
    ========================= */
    /* --- Buttons --- */
    button {
        background: none;
        border: none;
        border-radius: 0;
        color: inherit;
        cursor: pointer;
        font: inherit;
        padding: 0;
        transition: none;
    }
        
    /* Button Groups */
    .btn-group {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-base);
    }
    
    ${QUERIES.maxMobile} {
        .btn-group .btn {
            flex: 1;            /* Allow buttons to grow on mobile */
            text-align: center; /* Center text for better appearance when wrapped */
        }
    }

    /* Social Media Buttons */
    .btn--social {
        border-radius: var(--radius-circle);
        background: var(--color-neutral-darkest);
        color: var(--color-neutral-white);
        padding: var(--button-padding-icon);
        transition: transform 0.2s ease, background-color 0.2s ease;
    }

    .btn--social:hover {
        transform: var(--hover-scale);
    }

    .btn--social.btn--facebook:hover {
        background: var(--color-facebook);
    }

    .btn--social.btn--instagram:hover {
        background: var(--color-instagram);
    }

    .btn--social.btn--meetup:hover {
        background: var(--color-meetup);
    }

    /* --- Inputs --- */
    input, textarea {
        background-color: var(--color-neutral-white);
        border: var( --border-default);
        border-radius: var(--radius-input);
        padding: var(--spacing-small) var(--spacing-base);
        transition: var(--transition-all-ease);
        font-family: var(--font-family);
        font-style: normal;
        font-weight: var(--font-weight-regular);
        font-size: var(--body-base-size);
        line-height: 150%;
        
        &::placeholder {
            color: var(--color-primary-darker);
        }

        &:focus {
            border-color: var(--color-primary);
            outline: none;
            transform: scale(1.01);
        }
    }

    /* --- Lists --- */
    ul, ol {
        list-style: none;
        padding: 0;
        text-decoration: none;
    }
    
    /* --- Scrollbar --- */
    ::-webkit-scrollbar {
        width: var(--width-scrollbar);
    }

    ::-webkit-scrollbar-track {
        background: var(--color-neutral-lighter);
    }

    ::-webkit-scrollbar-thumb {
        background: var(--color-neutral);
        border-radius: var(--radius-base);
    }

    ::-webkit-scrollbar-thumb:hover {
        background: var(--color-neutral-dark);
    }

    /* =========================
       7. Utility Classes
    ========================= */
    .hidden {
        display: none !important;
    }
    
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
    
    .scroll-lock {
        overflow: hidden;
        position: fixed;
        width: 100%;
    }

    /* =========================
       8. Vendor Specific
    ========================= */
    /* --- Gatsby --- */
    .gatsby-image-wrapper img[src*=base64\\] {
        image-rendering: -moz-crisp-edges;
        image-rendering: pixelated;
    }

    /* =========================
       9. Accessibility Enhancements
    ========================= */
    @media (prefers-reduced-motion: reduce) {
    *, ::before, ::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
    }
    
    .btn:hover, .btn:active {
        transform: none !important;
    }
}
`;

export default GlobalStyles;
